import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Shield, 
  Store, 
  Users, 
  UtensilsCrossed,
  TrendingUp,
  Ban,
  CheckCircle,
  Loader2,
  Search,
  Trash2,
  Crown,
  UserCog,
  AlertTriangle,
  RefreshCw,
  Plus,
  Mail,
  Play,
  Edit3,
  Lock,
  Building,
  CreditCard
} from 'lucide-react';

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, restaurant: null });
  const [planModal, setPlanModal] = useState({ open: false, restaurant: null, newPlan: '', startDate: '', endDate: '' });
  const [roleModal, setRoleModal] = useState({ open: false, user: null, newRole: '' });
  const [createModal, setCreateModal] = useState({ open: false });
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    restaurant_name: '',
    plan: 'unique',
    payment_method: 'manual'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, restaurantsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/restaurants'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setRestaurants(restaurantsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé - Vous n\'êtes pas administrateur');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSuspend = async (restaurantId, currentStatus) => {
    setActionLoading(restaurantId);
    try {
      const endpoint = currentStatus 
        ? `/admin/restaurants/${restaurantId}/reactivate`
        : `/admin/restaurants/${restaurantId}/suspend`;
      await api.put(endpoint);
      toast.success(currentStatus ? 'Restaurant réactivé' : 'Restaurant suspendu');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'action');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePlan = async () => {
    if (!planModal.restaurant || !planModal.newPlan) return;
    setActionLoading(planModal.restaurant.id);
    try {
      await api.put(`/admin/restaurants/${planModal.restaurant.id}/plan`, { 
        plan_id: planModal.newPlan,
        subscription_start: planModal.startDate || null,
        subscription_end: planModal.endDate || null
      });
      toast.success(`Plan changé vers ${planModal.newPlan}`);
      setPlanModal({ open: false, restaurant: null, newPlan: '', startDate: '', endDate: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du changement de plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async () => {
    if (!roleModal.user || !roleModal.newRole) return;
    setActionLoading(roleModal.user.id);
    try {
      await api.put(`/admin/users/${roleModal.user.id}/role`, { role: roleModal.newRole });
      toast.success(`Rôle changé vers ${roleModal.newRole}`);
      setRoleModal({ open: false, user: null, newRole: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du changement de rôle');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!deleteModal.restaurant) return;
    setActionLoading(deleteModal.restaurant.id);
    try {
      await api.delete(`/admin/restaurants/${deleteModal.restaurant.id}`);
      toast.success('Restaurant supprimé');
      setDeleteModal({ open: false, restaurant: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateRestaurant = async () => {
    if (!createForm.email || !createForm.password || !createForm.restaurant_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/admin/create-restaurant', createForm);
      toast.success(`Restaurant "${response.data.restaurant_name}" créé avec succès !`);
      setCreateModal({ open: false });
      setCreateForm({
        email: '',
        password: '',
        restaurant_name: '',
        plan: 'unique',
        payment_method: 'manual'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const activatePlan = async (restaurantId, status = 'active') => {
    setActionLoading(restaurantId);
    try {
      await api.post(`/admin/restaurants/${restaurantId}/activate`, { status });
      toast.success(`Plan activé avec succès (${status === 'trial' ? 'Essai' : 'Actif'})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(null);
    }
  };

  const changePlan = async (restaurantId, plan) => {
    setActionLoading(restaurantId);
    try {
      await api.post(`/admin/restaurants/${restaurantId}/change-plan`, { plan });
      toast.success(`Plan changé en ${plan}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de plan');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredRestaurants = restaurants.filter(r => 
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.owner_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.restaurant_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid sm:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="h-8 w-8 text-orange-500" />
            Panel Admin
          </h1>
          <p className="text-gray-500 mt-1">
            Gestion de la plateforme ZOTMENU
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setCreateModal({ open: true })}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="create-restaurant-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un compte
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-0 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Restaurants</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-restaurants">{stats?.total_restaurants || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-orange-50 text-orange-500">
                <Store className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Utilisateurs</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-users">{stats?.total_users || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                <Users className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total plats</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-dishes">{stats?.total_dishes || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 text-emerald-500">
                <UtensilsCrossed className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Annuel</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-premium">
                  {(stats?.subscriptions_by_plan?.annuel || 0) + (stats?.subscriptions_by_plan?.premium || 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-orange-50 text-orange-500">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Suspendus</p>
                <p className="text-3xl font-bold text-red-600" data-testid="stat-suspended">{stats?.suspended_restaurants || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-50 text-red-500">
                <Ban className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription breakdown */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Répartition des abonnements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[120px] p-4 bg-orange-50 rounded-xl">
              <p className="text-sm text-orange-600">Formule unique</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.subscriptions_by_plan?.unique || 0}</p>
              <p className="text-xs text-orange-400 mt-1">19,90€ HT/mois</p>
            </div>
            {((stats?.subscriptions_by_plan?.annuel || 0) + (stats?.subscriptions_by_plan?.mensuel || 0) + (stats?.subscriptions_by_plan?.premium || 0) + (stats?.subscriptions_by_plan?.essentiel || 0) + (stats?.subscriptions_by_plan?.starter || 0)) > 0 && (
              <div className="flex-1 min-w-[120px] p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">Anciens forfaits</p>
                <p className="text-2xl font-bold text-gray-900">{(stats?.subscriptions_by_plan?.annuel || 0) + (stats?.subscriptions_by_plan?.mensuel || 0) + (stats?.subscriptions_by_plan?.premium || 0) + (stats?.subscriptions_by_plan?.essentiel || 0) + (stats?.subscriptions_by_plan?.starter || 0)}</p>
                <p className="text-xs text-gray-400 mt-1">À migrer vers la formule unique</p>
              </div>
            )}
          </div>
          {stats?.recent_signups > 0 && (
            <p className="text-sm text-emerald-600 mt-4">
              +{stats.recent_signups} nouvelle(s) inscription(s) cette semaine
            </p>
          )}
        </CardContent>
      </Card>

      {/* Expiring Subscriptions Alert */}
      {((stats?.expiring_subscriptions?.length > 0) || (stats?.expired_subscriptions?.length > 0)) && (
        <Card className="border-0 shadow-soft border-l-4 border-l-amber-500">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-600 mt-0.5">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-gray-900 mb-2">Alertes forfaits</h3>
                {stats?.expired_subscriptions?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-red-600 mb-1">Forfaits expirés</p>
                    {stats.expired_subscriptions.map(r => (
                      <div key={r.id} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
                        <span className="text-gray-700 font-medium">{r.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive" className="text-xs">
                            Expiré le {new Date(r.subscription_end).toLocaleDateString('fr-FR')}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">{r.subscription_plan}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {stats?.expiring_subscriptions?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-amber-600 mb-1">Forfaits expirant dans les 7 prochains jours</p>
                    {stats.expiring_subscriptions.map(r => (
                      <div key={r.id} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
                        <span className="text-gray-700 font-medium">{r.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs border-amber-200 text-amber-600 bg-amber-50">
                            Expire le {new Date(r.subscription_end).toLocaleDateString('fr-FR')}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">{r.subscription_plan}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Rechercher un restaurant ou utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="restaurants" className="space-y-4">
        <TabsList>
          <TabsTrigger value="restaurants" data-testid="tab-restaurants">
            <Store className="h-4 w-4 mr-2" />
            Restaurants ({filteredRestaurants.length})
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="h-4 w-4 mr-2" />
            Utilisateurs ({filteredUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* Restaurants Tab */}
        <TabsContent value="restaurants">
          <Card className="border-0 shadow-soft">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Restaurant</TableHead>
                      <TableHead>Propriétaire</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Créé le</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRestaurants.map((restaurant) => (
                      <TableRow key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{restaurant.name}</p>
                            <p className="text-sm text-gray-500">{restaurant.slug}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {restaurant.owner_email}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {restaurant.phone || '—'}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={`cursor-pointer hover:bg-gray-50 ${
                              restaurant.subscription_plan === 'unique' || restaurant.subscription_plan === 'annuel' || restaurant.subscription_plan === 'premium'
                                ? 'border-orange-200 text-orange-600' 
                                : ''
                            }`}
                            onClick={() => setPlanModal({ 
                              open: true, 
                              restaurant, 
                              newPlan: restaurant.subscription_plan,
                              startDate: restaurant.subscription_start || '',
                              endDate: restaurant.subscription_end || ''
                            })}
                          >
                            <Crown className="h-3 w-3 mr-1" />
                            {restaurant.subscription_plan}
                          </Badge>
                          {restaurant.subscription_end && (
                            <p className={`text-[10px] mt-0.5 ${
                              new Date(restaurant.subscription_end) < new Date() 
                                ? 'text-red-500 font-medium' 
                                : new Date(restaurant.subscription_end) < new Date(Date.now() + 7*86400000) 
                                  ? 'text-amber-500'
                                  : 'text-gray-400'
                            }`}>
                              {new Date(restaurant.subscription_end) < new Date() ? 'Expiré' : 'Fin'} : {new Date(restaurant.subscription_end).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {new Date(restaurant.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          {restaurant.suspended ? (
                            <Badge variant="destructive">Suspendu</Badge>
                          ) : restaurant.subscription_status === 'pending' ? (
                            <Badge variant="outline" className="border-amber-200 text-amber-600 bg-amber-50">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              En attente
                            </Badge>
                          ) : restaurant.subscription_status === 'trial' ? (
                            <Badge variant="outline" className="border-blue-200 text-blue-600 bg-blue-50">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Essai
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-200 text-emerald-600 bg-emerald-50">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Actif
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Activate button - only show if pending */}
                            {restaurant.subscription_status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => activatePlan(restaurant.id, 'active')}
                                disabled={actionLoading === restaurant.id}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                data-testid={`activate-${restaurant.id}`}
                                title="Activer le plan"
                              >
                                {actionLoading === restaurant.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Play className="h-4 w-4 mr-1" />
                                    Activer
                                  </>
                                )}
                              </Button>
                            )}
                            
                            {/* Change plan dropdown */}
                            <Select
                              value={restaurant.subscription_plan}
                              onValueChange={(value) => changePlan(restaurant.id, value)}
                              disabled={actionLoading === restaurant.id}
                            >
                              <SelectTrigger className="w-[100px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="annuel">Annuel</SelectItem>
                                <SelectItem value="mensuel">Mensuel</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {/* Suspend button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleSuspend(restaurant.id, restaurant.suspended)}
                              disabled={actionLoading === restaurant.id}
                              className={restaurant.suspended ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-600 hover:text-amber-700'}
                              data-testid={`toggle-suspend-${restaurant.id}`}
                              title={restaurant.suspended ? 'Réactiver' : 'Suspendre'}
                            >
                              {actionLoading === restaurant.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : restaurant.suspended ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <Ban className="h-4 w-4" />
                              )}
                            </Button>
                            
                            {/* Delete button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteModal({ open: true, restaurant })}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-restaurant-${restaurant.id}`}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredRestaurants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          {searchTerm ? 'Aucun restaurant trouvé' : 'Aucun restaurant inscrit'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-0 shadow-soft">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Restaurant</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Inscrit le</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <p className="font-medium text-gray-900">{user.email}</p>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {user.restaurant_name}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={`cursor-pointer hover:bg-gray-50 ${
                              user.role === 'admin' 
                                ? 'border-red-200 text-red-600' 
                                : ''
                            }`}
                            onClick={() => setRoleModal({ 
                              open: true, 
                              user, 
                              newRole: user.role 
                            })}
                          >
                            {user.role === 'admin' ? (
                              <Shield className="h-3 w-3 mr-1" />
                            ) : (
                              <UserCog className="h-3 w-3 mr-1" />
                            )}
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRoleModal({ 
                              open: true, 
                              user, 
                              newRole: user.role 
                            })}
                            className="text-gray-600"
                            data-testid={`edit-role-${user.id}`}
                          >
                            <UserCog className="h-4 w-4 mr-1" />
                            Changer rôle
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          {searchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur inscrit'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, restaurant: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le restaurant <strong>{deleteModal.restaurant?.name}</strong> ?
              <br /><br />
              Cette action est <strong>irréversible</strong> et supprimera toutes les données associées :
              plats, catégories, promotions, analytics et utilisateurs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteModal({ open: false, restaurant: null })}
            >
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteRestaurant}
              disabled={actionLoading === deleteModal.restaurant?.id}
              data-testid="confirm-delete-btn"
            >
              {actionLoading === deleteModal.restaurant?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Modal */}
      <Dialog open={planModal.open} onOpenChange={(open) => setPlanModal({ open, restaurant: null, newPlan: '', startDate: '', endDate: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-orange-500" />
              Changer le forfait
            </DialogTitle>
            <DialogDescription>
              Restaurant : <strong>{planModal.restaurant?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Forfait</Label>
              <Select 
                value={planModal.newPlan} 
                onValueChange={(value) => setPlanModal({ ...planModal, newPlan: value })}
              >
                <SelectTrigger data-testid="plan-select">
                  <SelectValue placeholder="Sélectionner un plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unique">Formule unique (19,90€ HT/mois)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input 
                  type="date"
                  value={planModal.startDate}
                  onChange={(e) => setPlanModal({ ...planModal, startDate: e.target.value })}
                  data-testid="plan-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input 
                  type="date"
                  value={planModal.endDate}
                  onChange={(e) => setPlanModal({ ...planModal, endDate: e.target.value })}
                  data-testid="plan-end-date"
                />
              </div>
            </div>
            {planModal.endDate && new Date(planModal.endDate) < new Date() && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded-lg">
                <AlertTriangle className="h-4 w-4" />
                Attention : cette date est déjà passée
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPlanModal({ open: false, restaurant: null, newPlan: '', startDate: '', endDate: '' })}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleChangePlan}
              disabled={actionLoading === planModal.restaurant?.id}
              data-testid="confirm-plan-btn"
            >
              {actionLoading === planModal.restaurant?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Modal */}
      <Dialog open={roleModal.open} onOpenChange={(open) => setRoleModal({ open, user: null, newRole: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-blue-500" />
              Changer le rôle
            </DialogTitle>
            <DialogDescription>
              Utilisateur : <strong>{roleModal.user?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select 
              value={roleModal.newRole} 
              onValueChange={(value) => setRoleModal({ ...roleModal, newRole: value })}
            >
              <SelectTrigger data-testid="role-select">
                <SelectValue placeholder="Sélectionner un rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Propriétaire (Owner)</SelectItem>
                <SelectItem value="admin">Administrateur (Admin)</SelectItem>
              </SelectContent>
            </Select>
            {roleModal.newRole === 'admin' && (
              <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Un admin a accès à toutes les données de la plateforme
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setRoleModal({ open: false, user: null, newRole: '' })}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleChangeRole}
              disabled={actionLoading === roleModal.user?.id || roleModal.newRole === roleModal.user?.role}
              data-testid="confirm-role-btn"
            >
              {actionLoading === roleModal.user?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Restaurant Modal */}
      <Dialog open={createModal.open} onOpenChange={(open) => setCreateModal({ open })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-500" />
              Créer un compte restaurateur
            </DialogTitle>
            <DialogDescription>
              Créez un compte pour un restaurateur qui règle autrement que par Stripe (virement, chèque, espèces...).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant_name" className="flex items-center gap-2">
                <Building className="h-4 w-4 text-gray-400" />
                Nom du restaurant *
              </Label>
              <Input
                id="restaurant_name"
                placeholder="Ex: Le Petit Bistrot"
                value={createForm.restaurant_name}
                onChange={(e) => setCreateForm({ ...createForm, restaurant_name: e.target.value })}
                data-testid="create-restaurant-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                Email du propriétaire *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="proprietaire@restaurant.fr"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                data-testid="create-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-gray-400" />
                Mot de passe *
              </Label>
              <Input
                id="password"
                type="text"
                placeholder="Mot de passe temporaire"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                data-testid="create-password"
              />
              <p className="text-xs text-gray-500">
                Le restaurateur pourra changer ce mot de passe après connexion.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-gray-400" />
                  Plan d'abonnement
                </Label>
                <Select 
                  value={createForm.plan} 
                  onValueChange={(value) => setCreateForm({ ...createForm, plan: value })}
                >
                  <SelectTrigger data-testid="create-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unique">Formule unique (19,90€ HT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  Mode de paiement
                </Label>
                <Select 
                  value={createForm.payment_method} 
                  onValueChange={(value) => setCreateForm({ ...createForm, payment_method: value })}
                >
                  <SelectTrigger data-testid="create-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuel</SelectItem>
                    <SelectItem value="bank_transfer">Virement</SelectItem>
                    <SelectItem value="check">Chèque</SelectItem>
                    <SelectItem value="cash">Espèces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                Le compte sera activé immédiatement sans passer par Stripe. 
                Assurez-vous d'avoir reçu le paiement.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCreateModal({ open: false })}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCreateRestaurant}
              disabled={creating || !createForm.email || !createForm.password || !createForm.restaurant_name}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-create-btn"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
